import { useEffect, useRef, useState } from 'react';
import {
  Activity,
  Droplets,
  Heart,
  Thermometer,
  Wind,
  Scale,
  Gauge,
  X,
} from 'lucide-react';
import {
  getVitalCardClasses,
  getVitalFlagSeverity,
  getVitalValueClass,
} from '../../utils/clinicalFlags';
import { stripO2SatPercent } from '../../utils/vitalsParse';

const ICONS = {
  bp: Heart,
  pulse: Activity,
  temp_celsius: Thermometer,
  o2_sat: Droplets,
  rr: Wind,
  pain: Gauge,
  weight_kg: Scale,
  blood_sugar: Activity,
};

function formatBpValue(systolic, diastolic) {
  if (systolic != null && diastolic != null) return `${systolic}/${diastolic}`;
  if (systolic != null) return String(systolic);
  if (diastolic != null) return String(diastolic);
  return '';
}

function NumberBox({
  inputRef,
  label,
  value,
  onChange,
  onKeyDown,
  unit,
  ariaLabel,
}) {
  return (
    <label className="block min-w-0 flex-1">
      {label ? (
        <span className="mb-1 block text-[11px] font-medium text-gray-500">{label}</span>
      ) : null}
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onClick={(e) => e.stopPropagation()}
          placeholder="—"
          className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-[16px] font-semibold text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          aria-label={ariaLabel || label}
        />
        {unit ? <span className="shrink-0 text-xs text-gray-400">{unit}</span> : null}
      </div>
    </label>
  );
}

export default function VitalCard({
  vitalKey,
  label,
  unit,
  displayValue,
  flags = [],
  onSave,
  onDelete,
  allowDelete = true,
  placeholder = '—',
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(displayValue ?? '');
  const [bpDraft, setBpDraft] = useState({ systolic: '', diastolic: '' });
  const inputRef = useRef(null);
  const cardRef = useRef(null);
  const Icon = ICONS[vitalKey] || Activity;
  const severity = getVitalFlagSeverity(flags, vitalKey, displayValue);
  const isBp = vitalKey === 'bp';

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    setDraft(displayValue ?? '');
    if (isBp) {
      const parts = String(displayValue || '').split('/');
      setBpDraft({
        systolic: parts[0]?.trim() || '',
        diastolic: parts[1]?.trim() || '',
      });
    }
  }, [displayValue, isBp]);

  useEffect(() => {
    if (!editing) return undefined;
    const handleClickOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        if (isBp) {
          onSave({ systolic: bpDraft.systolic, diastolic: bpDraft.diastolic });
        } else {
          onSave(draft);
        }
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [bpDraft, draft, editing, isBp, onSave]);

  const commitAndClose = () => {
    if (isBp) {
      onSave({ systolic: bpDraft.systolic, diastolic: bpDraft.diastolic });
    } else {
      onSave(draft);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitAndClose();
    }
    if (e.key === 'Escape') {
      setDraft(displayValue ?? '');
      if (isBp) {
        const parts = String(displayValue || '').split('/');
        setBpDraft({
          systolic: parts[0]?.trim() || '',
          diastolic: parts[1]?.trim() || '',
        });
      }
      setEditing(false);
    }
  };

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={() => {
        if (!editing) setEditing(true);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !editing) setEditing(true);
      }}
      className={`${getVitalCardClasses(severity)} cursor-pointer transition-shadow active:shadow-md`}
    >
      <div className="flex items-center justify-between gap-2 text-gray-500">
        <div className="flex min-w-0 items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2} />
          <span className="truncate text-xs">{label}</span>
        </div>
        {allowDelete ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
            className="rounded-full p-1 hover:text-primary"
            aria-label={`Delete ${label}`}
          >
            <X className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
          </button>
        ) : null}
      </div>

      {editing && isBp ? (
        <div className="grid grid-cols-2 gap-2">
          <NumberBox
            inputRef={inputRef}
            label="Systolic"
            value={bpDraft.systolic}
            onChange={(value) => setBpDraft((prev) => ({ ...prev, systolic: value }))}
            onKeyDown={handleKeyDown}
            unit="mmHg"
            ariaLabel="Systolic"
          />
          <NumberBox
            label="Diastolic"
            value={bpDraft.diastolic}
            onChange={(value) => setBpDraft((prev) => ({ ...prev, diastolic: value }))}
            onKeyDown={handleKeyDown}
            unit="mmHg"
            ariaLabel="Diastolic"
          />
        </div>
      ) : editing ? (
        <NumberBox
          inputRef={inputRef}
          value={draft}
          onChange={setDraft}
          onKeyDown={handleKeyDown}
          unit={unit}
          ariaLabel={`Edit ${label}`}
        />
      ) : (
        <div className="flex items-baseline gap-1">
          <span className={`text-[20px] font-bold leading-none ${getVitalValueClass(severity)}`}>
            {displayValue || placeholder}
          </span>
          {unit && displayValue && <span className="text-xs text-gray-400">{unit}</span>}
        </div>
      )}
    </div>
  );
}

function clearVitalFields(vitals, key) {
  const next = { ...vitals };
  if (key === 'bp') {
    next.bp_systolic = null;
    next.bp_diastolic = null;
  } else {
    next[key] = null;
  }
  return next;
}

export function VitalsCardGrid({ note, onChange, flags = [], allowDelete = true }) {
  const vitals = note?.vitals || {};
  const hasManualKeys = Array.isArray(note?._manualVisibleVitalsKeys);
  const allowedKeys = hasManualKeys ? note._manualVisibleVitalsKeys : null;

  const commitVitals = (nextVitals, nextAllowedKeys = allowedKeys) => {
    onChange({
      ...note,
      vitals: nextVitals,
      ...(hasManualKeys ? { _manualVisibleVitalsKeys: nextAllowedKeys } : {}),
    });
  };

  const handleBpSave = (raw) => {
    const next = { ...vitals };
    if (raw && typeof raw === 'object') {
      const sys = raw.systolic === '' ? null : Number(raw.systolic);
      const dia = raw.diastolic === '' ? null : Number(raw.diastolic);
      next.bp_systolic = Number.isNaN(sys) ? null : sys;
      next.bp_diastolic = Number.isNaN(dia) ? null : dia;
    } else if (raw === '' || raw == null) {
      next.bp_systolic = null;
      next.bp_diastolic = null;
    }
    commitVitals(next);
  };

  const handleVitalSave = (key, raw) => {
    const parsed = raw === '' ? null : Number.isNaN(Number(raw)) ? raw : Number(raw);
    commitVitals({ ...vitals, [key]: parsed });
  };

  const handleDeleteVital = (key) => {
    const nextVitals = clearVitalFields(vitals, key);
    const nextAllowedKeys = hasManualKeys
      ? (allowedKeys || []).filter((item) => item !== key)
      : allowedKeys;
    commitVitals(nextVitals, nextAllowedKeys);
  };

  const hasValue = (v) => v != null && v !== '';

  const items = [
    hasValue(vitals.bp_systolic) || hasValue(vitals.bp_diastolic)
      ? {
          key: 'bp',
          label: 'Blood Pressure',
          unit: 'mmHg',
          displayValue: formatBpValue(vitals.bp_systolic, vitals.bp_diastolic),
          onSave: handleBpSave,
          onDelete: () => handleDeleteVital('bp'),
        }
      : null,
    hasValue(vitals.pulse)
      ? {
          key: 'pulse',
          label: 'Pulse',
          unit: 'bpm',
          displayValue: String(vitals.pulse),
          onSave: (v) => handleVitalSave('pulse', v),
          onDelete: () => handleDeleteVital('pulse'),
        }
      : null,
    hasValue(vitals.temp_celsius)
      ? {
          key: 'temp_celsius',
          label: 'Temp',
          unit: '°C',
          displayValue: String(vitals.temp_celsius),
          onSave: (v) => handleVitalSave('temp_celsius', v),
          onDelete: () => handleDeleteVital('temp_celsius'),
        }
      : null,
    hasValue(vitals.o2_sat)
      ? {
          key: 'o2_sat',
          label: 'O2 Sat',
          unit: '%',
          displayValue: String(stripO2SatPercent(vitals.o2_sat) ?? ''),
          onSave: (v) => handleVitalSave('o2_sat', stripO2SatPercent(v)),
          onDelete: () => handleDeleteVital('o2_sat'),
        }
      : null,
    hasValue(vitals.rr)
      ? {
          key: 'rr',
          label: 'RR',
          unit: '/min',
          displayValue: String(vitals.rr),
          onSave: (v) => handleVitalSave('rr', v),
          onDelete: () => handleDeleteVital('rr'),
        }
      : null,
    hasValue(vitals.pain)
      ? {
          key: 'pain',
          label: 'Pain',
          unit: '/10',
          displayValue: String(vitals.pain),
          onSave: (v) => handleVitalSave('pain', v),
          onDelete: () => handleDeleteVital('pain'),
        }
      : null,
    hasValue(vitals.weight_kg)
      ? {
          key: 'weight_kg',
          label: 'Weight',
          unit: 'kg',
          displayValue: String(vitals.weight_kg),
          onSave: (v) => handleVitalSave('weight_kg', v),
          onDelete: () => handleDeleteVital('weight_kg'),
        }
      : null,
    hasValue(vitals.blood_sugar)
      ? {
          key: 'blood_sugar',
          label: 'Blood Sugar',
          unit: 'mg/dL',
          displayValue: String(vitals.blood_sugar),
          onSave: (v) => handleVitalSave('blood_sugar', v),
          onDelete: () => handleDeleteVital('blood_sugar'),
        }
      : null,
  ].filter(Boolean);

  const fallbackByKey = {
    bp: {
      key: 'bp',
      label: 'Blood Pressure',
      unit: 'mmHg',
      displayValue: '',
      onSave: handleBpSave,
      onDelete: () => handleDeleteVital('bp'),
    },
    pulse: {
      key: 'pulse',
      label: 'Pulse',
      unit: 'bpm',
      displayValue: '',
      onSave: (v) => handleVitalSave('pulse', v),
      onDelete: () => handleDeleteVital('pulse'),
    },
    temp_celsius: {
      key: 'temp_celsius',
      label: 'Temp',
      unit: '°C',
      displayValue: '',
      onSave: (v) => handleVitalSave('temp_celsius', v),
      onDelete: () => handleDeleteVital('temp_celsius'),
    },
    o2_sat: {
      key: 'o2_sat',
      label: 'O2 Sat',
      unit: '%',
      displayValue: '',
      onSave: (v) => handleVitalSave('o2_sat', v),
      onDelete: () => handleDeleteVital('o2_sat'),
    },
    rr: {
      key: 'rr',
      label: 'RR',
      unit: '/min',
      displayValue: '',
      onSave: (v) => handleVitalSave('rr', v),
      onDelete: () => handleDeleteVital('rr'),
    },
    pain: {
      key: 'pain',
      label: 'Pain',
      unit: '/10',
      displayValue: '',
      onSave: (v) => handleVitalSave('pain', v),
      onDelete: () => handleDeleteVital('pain'),
    },
    weight_kg: {
      key: 'weight_kg',
      label: 'Weight',
      unit: 'kg',
      displayValue: '',
      onSave: (v) => handleVitalSave('weight_kg', v),
      onDelete: () => handleDeleteVital('weight_kg'),
    },
    blood_sugar: {
      key: 'blood_sugar',
      label: 'Blood Sugar',
      unit: 'mg/dL',
      displayValue: '',
      onSave: (v) => handleVitalSave('blood_sugar', v),
      onDelete: () => handleDeleteVital('blood_sugar'),
    },
  };

  const itemByKey = new Map(items.map((item) => [item.key, item]));

  let keysToShow = [];
  if (hasManualKeys) {
    keysToShow = Array.from(
      new Set([
        ...(allowedKeys || []),
        ...items.map((item) => item.key),
      ])
    );
  } else if (items.length) {
    keysToShow = items.map((item) => item.key);
  } else {
    keysToShow = [];
  }

  const defaultItems = keysToShow
    .map((key) => itemByKey.get(key) || fallbackByKey[key])
    .filter(Boolean);

  if (!defaultItems.length) return null;

  return (
    <div className="flex flex-col gap-2.5">
      {defaultItems.map((item) => (
        <VitalCard
          key={item.key}
          vitalKey={item.key}
          label={item.label}
          unit={item.unit}
          displayValue={item.displayValue}
          flags={flags}
          onSave={item.onSave}
          onDelete={item.onDelete}
          allowDelete={allowDelete}
        />
      ))}
    </div>
  );
}
